import React, { useState, useEffect } from "react";
import { db, auth, functions } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  getDoc,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { isAdminUser } from "../lib/admin";

const AUCTION_BASE_URL = "https://auction.lepingouin.be";
const AUCTION_2026_END_DATE = new Date("2026-04-13T16:00:00+02:00");
const AUCTION_2026_ITEMS = [
  {
    id: "polo-pingouin-girl-m-helena-1",
    slug: "polo-pingouin-girl-m-helena-1",
    name: 'POLO PINGOUIN Girl "M" signé par Héléna (Lot 1)',
    description: 'POLO PINGOUIN Girl "M" signé par Héléna.',
    startingPrice: 0,
  },
  {
    id: "polo-pingouin-girl-m-helena-2",
    slug: "polo-pingouin-girl-m-helena-2",
    name: 'POLO PINGOUIN Girl "M" signé par Héléna (Lot 2)',
    description: 'POLO PINGOUIN Girl "M" signé par Héléna.',
    startingPrice: 0,
  },
  {
    id: "maillot-vincent-vansach-21",
    slug: "maillot-vincent-vansach-21",
    name: "Maillot de Vincent Vansach #21",
    description: "Maillot de Vincent Vansach #21.",
    startingPrice: 0,
  },
  {
    id: "stick-thurso-lb250-36-5-blue-helena",
    slug: "stick-thurso-lb250-36-5-blue-helena",
    name: 'Stick Thurso LB250 - 36’5 - "Blue" signé par Héléna',
    description: 'Stick Thurso LB250 - 36’5 - "Blue" signé par Héléna.',
    startingPrice: 0,
  },
  {
    id: "stick-thurso-lb250-36-5-rainbow-helena",
    slug: "stick-thurso-lb250-36-5-rainbow-helena",
    name: 'Stick Thurso LB250 - 36’5 - "Rainbow" signé par Héléna',
    description: 'Stick Thurso LB250 - 36’5 - "Rainbow" signé par Héléna.',
    startingPrice: 0,
  },
];

export default function Admin() {
  const [items, setItems] = useState([]);
  const [bids, setBids] = useState([]);
  const [users, setUsers] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    image: "",
    startingPrice: 0,
    endDate: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authError, setAuthError] = useState("");
  const [isSigningInWithGoogle, setIsSigningInWithGoogle] = useState(false);
  const [isLoadingSms, setIsLoadingSms] = useState({});
  const [isSeedingAuction2026, setIsSeedingAuction2026] = useState(false);

  // Check authorization on mount and auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsAuthorized(isAdminUser(user));
      setAuthResolved(true);
    });

    return () => unsubscribe();
  }, []);

  // Load items
  useEffect(() => {
    if (!isAuthorized) return;
    const unsub = onSnapshot(collection(db, "items"), (snap) =>
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [isAuthorized]);

  // Load bids and user information
  useEffect(() => {
    if (!isAuthorized) return;
    const unsub = onSnapshot(
      query(collection(db, "bids"), orderBy("placedAt", "desc")),
      async (snap) => {
        const bidsData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBids(bidsData);

        // Fetch user information for each bid
        const userIds = [...new Set(bidsData.map((bid) => bid.userId))];
        const usersData = {};
        for (const userId of userIds) {
          try {
            const userDoc = await getDoc(doc(db, "users", userId));
            if (userDoc.exists()) {
              usersData[userId] = userDoc.data().name;
            }
          } catch (error) {
            console.error("Error fetching user:", error);
          }
        }
        setUsers(usersData);
      }
    );
    return unsub;
  }, [isAuthorized]);

  // Load users
  useEffect(() => {
    if (!isAuthorized) return;
    const unsub = onSnapshot(
      query(collection(db, "users"), orderBy("createdAt", "desc")),
      (snap) => {
        const usersData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllUsers(usersData);
      }
    );
    return unsub;
  }, [isAuthorized]);

  // Group bids by item and find highest bid for each item
  const getHighestBids = () => {
    const groupedBids = {};
    for (const bid of bids) {
      if (!groupedBids[bid.itemId]) {
        groupedBids[bid.itemId] = {
          highestBid: bid.amount,
          totalBids: 1,
          lastBid: bid.placedAt,
        };
      } else {
        groupedBids[bid.itemId].highestBid = Math.max(
          groupedBids[bid.itemId].highestBid,
          bid.amount
        );
        groupedBids[bid.itemId].totalBids += 1;
        groupedBids[bid.itemId].lastBid = bid.placedAt;
      }
    }
    return groupedBids;
  };

  const resetForm = () => {
    setForm({
      name: "",
      slug: "",
      description: "",
      image: "",
      startingPrice: 0,
      endDate: "",
    });
    setEditingId(null);
  };

  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create a copy of the form data to modify
    const formData = { ...form };

    // Convert endDate string to Firestore Timestamp if it exists
    if (formData.endDate) {
      const dateObj = new Date(formData.endDate);
      formData.endDate = Timestamp.fromDate(dateObj);
    }

    if (editingId) {
      await updateDoc(doc(db, "items", editingId), formData);
    } else {
      await addDoc(collection(db, "items"), formData);
    }
    resetForm();
  };

  const handleEdit = (item) => {
    // If endDate is a Timestamp, convert it to a string in the datetime-local format
    let endDateStr = "";
    if (item.endDate) {
      // Check if endDate is a Timestamp object (has toDate method)
      if (item.endDate.toDate) {
        const date = new Date(item.endDate.seconds * 1000);
        endDateStr = formatDateTimeLocal(date);
      } else if (typeof item.endDate === "string") {
        // If it's already a string (might happen during development/testing)
        endDateStr = item.endDate;
      }
    }

    setForm({
      name: item.name,
      slug: item.slug,
      description: item.description,
      image: item.image,
      startingPrice: item.startingPrice,
      endDate: endDateStr,
    });
    setEditingId(item.id);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "items", id));
    if (editingId === id) resetForm();
  };

  const handleDeleteBid = async (bidId) => {
    try {
      await deleteDoc(doc(db, "bids", bidId));
    } catch (error) {
      console.error("Error deleting bid:", error);
    }
  };

  const highestBids = getHighestBids();

  // Helper function to format dates consistently
  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    try {
      // Handle Firestore Timestamp
      if (dateValue.toDate && typeof dateValue.toDate === "function") {
        return dateValue.toDate().toLocaleString();
      }
      // Handle string date
      if (typeof dateValue === "string") {
        return new Date(dateValue).toLocaleString();
      }
      // Handle Date object
      if (dateValue instanceof Date) {
        return dateValue.toLocaleString();
      }
      // Fallback
      return String(dateValue);
    } catch (error) {
      console.error("Error formatting date:", error);
      return String(dateValue);
    }
  };

  // Add function to notify bidders about auction ending soon
  const handleNotifyEndingSoon = async (itemId, itemName, itemSlug) => {
    setIsLoadingSms((prev) => ({ ...prev, [itemId]: true }));
    try {
      const notifyEndingSoon = httpsCallable(
        functions,
        "notifyAuctionEndingSoon"
      );
      const result = await notifyEndingSoon({ itemId, itemName, itemSlug });
      console.log("Notification result:", result.data);
      alert(`Notifications envoyées à ${result.data.sentCount} enchérisseurs!`);
    } catch (error) {
      console.error("Error sending notifications:", error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setIsLoadingSms((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleSetupAuction2026 = async () => {
    setIsSeedingAuction2026(true);

    try {
      const batch = writeBatch(db);
      const endDate = Timestamp.fromDate(AUCTION_2026_END_DATE);

      for (const item of AUCTION_2026_ITEMS) {
        const payload = {
          slug: item.slug,
          name: item.name,
          description: item.description,
          startingPrice: item.startingPrice,
          endDate,
        };
        batch.set(doc(db, "items", item.id), payload, { merge: true });
      }

      await batch.commit();
      alert("Les 5 lots 2026 sont prets.");
    } catch (error) {
      console.error("Error setting up 2026 auction:", error);
      alert(`Erreur lors de la creation des lots: ${error.message}`);
    } finally {
      setIsSeedingAuction2026(false);
    }
  };

  const handleGoogleAdminSignIn = async () => {
    setAuthError("");
    setIsSigningInWithGoogle(true);

    try {
      if (auth.currentUser && !isAuthorized) {
        await signOut(auth);
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google:", error);
      let message = error.message || "Connexion Google impossible.";
      if (error.code === "auth/operation-not-allowed") {
        message =
          "La connexion Google n'est pas active dans Firebase Authentication.";
      } else if (error.code === "auth/popup-closed-by-user") {
        message = "La fenêtre Google a été fermée avant la connexion.";
      }
      setAuthError(message);
    } finally {
      setIsSigningInWithGoogle(false);
    }
  };

  const handleAdminSignOut = async () => {
    setAuthError("");
    await signOut(auth);
  };

  if (!authResolved) {
    return <div className="p-6">Vérification de l'accès admin...</div>;
  }

  if (!isAuthorized) {
    const accountLabel =
      currentUser?.email || currentUser?.phoneNumber || currentUser?.uid || "";

    return (
      <div className="p-6 min-h-screen bg-orange-50">
        <div className="max-w-lg mx-auto bg-white rounded-xl shadow p-6">
          <h1 className="text-3xl font-bold mb-4">Admin</h1>
          <p className="text-gray-700 mb-4">
            Connectez-vous avec un compte administrateur autorisé pour accéder
            à la gestion des enchères.
          </p>
          {accountLabel ? (
            <p className="text-sm text-gray-600 mb-4">
              Compte actuel: {accountLabel}
            </p>
          ) : null}
          {authError ? (
            <p className="text-sm text-red-600 mb-4">{authError}</p>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleGoogleAdminSignIn}
              disabled={isSigningInWithGoogle}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded disabled:opacity-50"
            >
              {isSigningInWithGoogle
                ? "Connexion..."
                : "Se connecter avec Google"}
            </button>
            {currentUser ? (
              <button
                type="button"
                onClick={handleAdminSignOut}
                className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-2 px-4 rounded"
              >
                Se déconnecter
              </button>
            ) : null}
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 rounded border border-gray-300 text-gray-700"
            >
              Retour au site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">
        Admin - Gestion des articles et des enchères
      </h1>
      <section className="mb-8 bg-orange-50 border border-orange-200 rounded-xl p-6">
        <h2 className="text-2xl font-semibold mb-2">Lots 2026</h2>
        <p className="text-gray-700 mb-4">
          Cette action met en place les 5 lots pour la vente en cours et fixe
          la fin des encheres au 13 avril 2026 a 16:00 Europe/Brussels.
        </p>
        <button
          type="button"
          onClick={handleSetupAuction2026}
          disabled={isSeedingAuction2026}
          className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded disabled:opacity-50"
        >
          {isSeedingAuction2026
            ? "Creation en cours..."
            : "Creer / mettre a jour les 5 lots 2026"}
        </button>
        <div className="mt-6">
          <h3 className="font-semibold mb-2">Liens des lots</h3>
          <ul className="space-y-2 text-sm break-all">
            {AUCTION_2026_ITEMS.map((item) => (
              <li key={item.slug}>
                <a
                  className="text-blue-600 underline"
                  href={`${AUCTION_BASE_URL}/item/${item.slug}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.name}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm">
            <Link to="/qrcodes" className="text-blue-600 underline">
              Ouvrir la page des QR codes
            </Link>
          </p>
        </div>
      </section>
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Utilisateurs</h2>
        <table className="w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Nom</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Téléphone</th>
              <th className="p-2 border">Date d'inscription</th>
            </tr>
          </thead>
          <tbody>
            {allUsers.map((user) => (
              <tr key={user.id}>
                <td className="p-2 border">{user.id}</td>
                <td className="p-2 border">{user.name}</td>
                <td className="p-2 border">{user.email || "-"}</td>
                <td className="p-2 border">{user.phoneNumber || "-"}</td>
                <td className="p-2 border">
                  {user.createdAt
                    ? new Date(user.createdAt.toDate()).toLocaleString()
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Articles</h2>
        <form
          onSubmit={handleSubmit}
          className="mb-6 bg-white p-4 rounded shadow"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border p-2"
              required
            />
            <input
              type="text"
              placeholder="Slug"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="border p-2"
              required
            />
            <input
              type="text"
              placeholder="Image URL"
              value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
              className="border p-2"
            />
            <input
              type="number"
              placeholder="Starting Price"
              value={form.startingPrice}
              onChange={(e) =>
                setForm({ ...form, startingPrice: Number(e.target.value) })
              }
              className="border p-2"
              required
            />
            <input
              type="datetime-local"
              placeholder="End Date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              className="border p-2"
              required
            />
            <textarea
              placeholder="Description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="border p-2 col-span-full"
            />
          </div>
          <button
            type="submit"
            className="mt-4 bg-green-500 text-white py-2 px-4 rounded"
          >
            {editingId ? "Mettre à jour" : "Ajouter"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="ml-4 bg-gray-300 py-2 px-4 rounded"
            >
              Annuler
            </button>
          )}
        </form>
        <table className="w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Slug</th>
              <th className="p-2 border">Starting Price</th>
              <th className="p-2 border">End Date</th>
              <th className="p-2 border">Highest Bid</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td className="p-2 border">{item.id}</td>
                <td className="p-2 border">{item.name}</td>
                <td className="p-2 border">{item.slug}</td>
                <td className="p-2 border">{item.startingPrice}€</td>
                <td className="p-2 border">{formatDate(item.endDate)}</td>
                <td className="p-2 border">
                  {highestBids[item.id]
                    ? `${highestBids[item.id].highestBid}€`
                    : "-"}
                </td>
                <td className="p-2 border">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className="text-blue-500 mr-2"
                  >
                    Éditer
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    className="text-red-500 mr-2"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleNotifyEndingSoon(item.id, item.name, item.slug)
                    }
                    className="text-orange-500 bg-orange-100 px-2 py-1 rounded"
                    disabled={isLoadingSms[item.id]}
                  >
                    {isLoadingSms[item.id] ? "Envoi..." : "Notifier fin"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section>
        <h2 className="text-2xl font-semibold mb-4">Enchères par article</h2>
        <div className="space-y-4">
          {items.map((item) => {
            const itemBids = bids.filter((bid) => bid.itemId === item.id);
            return itemBids.length > 0 ? (
              <div key={item.id} className="bg-white p-4 rounded shadow">
                <h3 className="font-semibold mb-2">
                  {item.name} (ID: {item.id})
                </h3>
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="p-2 border">Bidder</th>
                      <th className="p-2 border">Montant</th>
                      <th className="p-2 border">Timestamp</th>
                      <th className="p-2 border">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemBids.map((bid) => (
                      <tr key={bid.id}>
                        <td className="p-2 border">
                          {users[bid.userId] || bid.bidder}
                        </td>
                        <td className="p-2 border">{bid.amount}€</td>
                        <td className="p-2 border">
                          {bid.placedAt?.toDate().toLocaleString()}
                        </td>
                        <td className="p-2 border">
                          <button
                            type="button"
                            onClick={() => handleDeleteBid(bid.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null;
          })}
        </div>
      </section>
    </div>
  );
}
